import { useQuery } from "@apollo/client";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import useHasRole, {
  useIsCurrentUserDistributor,
} from "../../lib/hooks/useHasRole";
import { useClientId } from "../../pages/Clients/Client/hooks/useClient";
import { getClientFeatureQuery } from "../../config/graphql/query";

type PreferencesFieldValues = {
  preferences?:
    | IMemberGroupPreferences
    | IClientPreferences
    | ICompanyPreferences; // remove this later
};

export const FormCheckbox = (props: {
  form: {
    field: ControllerRenderProps<PreferencesFieldValues, any>;
    fieldState: ControllerFieldState;
  };
  disabled?: boolean;
  label: React.ReactNode;
}) => {
  const {
    form: {
      field: { value, name, onChange },
      fieldState: { error },
    },
    label,
    disabled,
  } = props;

  return (
    <div className="form-group form-check mb-1">
      <input
        id={name}
        type="checkbox"
        className="form-check-input"
        checked={value}
        disabled={disabled}
        onChange={onChange}
      />
      <label className="form-check-label user-select-none" htmlFor={name}>
        {label}
      </label>
      {!!error && <div className="invalid-feedback">{error.message}</div>}
    </div>
  );
};

const PreferencesChannelSettings = (props: {
  name: string;
  parent?: IPreferenceChannels;
  disabled?: boolean;
  isAuto?: boolean;
}) => {
  const { name, parent, disabled, isAuto } = props;

  const clientId = useClientId();

  const { data } = useQuery(getClientFeatureQuery("teams"), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const teamsEnabled = data?.client?.preferences?.teams?.enabled ?? false;

  const { data: googleChatData } = useQuery(
    getClientFeatureQuery("googleChat"),
    {
      variables: { id: clientId },
      context: { headers: { ClientId: clientId } },
      fetchPolicy: "network-only",
    },
  );

  const googleChatEnabled =
    googleChatData?.client?.preferences?.googleChat?.enabled ?? false;

  const { t } = useTranslation(["preferenceChannels"]);

  const isAdmin = useHasRole(["ADMIN"]);
  const isDistributor = useIsCurrentUserDistributor();

  const enabled = useWatch({ name: `${name}.enabled` });

  const autoCall = useWatch({ name: `${name}.autoChannel.call` });
  const autoPing = useWatch({ name: `${name}.autoChannel.ping` });

  if (!enabled) {
    return null;
  }

  return (
    <div className="ml-4">
      <h6>{t(`preferenceChannels:title.${isAuto ? "auto" : "default"}`)}</h6>
      {!!isAuto && (
        <Controller
          name={`${name}.autoChannel.email`}
          defaultValue={false}
          render={(form) => (
            <FormCheckbox
              form={form}
              label={t("preferenceChannels:email")}
              disabled={disabled || parent?.email === false}
            />
          )}
        />
      )}
      <Controller
        name={`${name}.${isAuto ? "autoChannel" : "channel"}.sms`}
        defaultValue={false}
        render={(form) => (
          <FormCheckbox
            form={form}
            label={t("preferenceChannels:sms")}
            disabled={disabled || parent?.sms === false}
          />
        )}
      />
      <Controller
        name={`${name}.${isAuto ? "autoChannel" : "channel"}.call`}
        defaultValue={false}
        render={(form) => (
          <FormCheckbox
            form={form}
            label={t("preferenceChannels:call")}
            disabled={
              disabled ||
              parent?.call === false ||
              (isAuto && autoPing === true)
            }
          />
        )}
      />
      <Controller
        name={`${name}.${isAuto ? "autoChannel" : "channel"}.ping`}
        defaultValue={false}
        render={(form) => (
          <FormCheckbox
            form={form}
            label={t("preferenceChannels:ping")}
            disabled={
              disabled ||
              parent?.ping === false ||
              (isAuto && autoCall === true)
            }
          />
        )}
      />
      {teamsEnabled && (
        <>
          <Controller
            name={`${name}.${isAuto ? "autoChannel" : "channel"}.teamsCall`}
            defaultValue={false}
            render={(form) => (
              <FormCheckbox
                form={form}
                label={t("preferenceChannels:teamsCall")}
                disabled={disabled || parent?.teamsCall === false}
              />
            )}
          />
          <Controller
            name={`${name}.${isAuto ? "autoChannel" : "channel"}.teamsMessage`}
            defaultValue={false}
            render={(form) => (
              <FormCheckbox
                form={form}
                label={t("preferenceChannels:teamsMessage")}
                disabled={disabled || parent?.teamsMessage === false}
              />
            )}
          />
        </>
      )}
      {googleChatEnabled && (
        <Controller
          name={`${name}.${
            isAuto ? "autoChannel" : "channel"
          }.googleChatMessage`}
          defaultValue={false}
          render={(form) => (
            <FormCheckbox
              form={form}
              label={t("preferenceChannels:googleChatMessage")}
              disabled={disabled || parent?.googleChatMessage === false}
            />
          )}
        />
      )}
      {(isAdmin || isDistributor) && (
        <Controller
          name={`${name}.${isAuto ? "autoChannel" : "channel"}.videoCall`}
          defaultValue={false}
          render={(form) => (
            <FormCheckbox
              form={form}
              label={t("preferenceChannels:videoCall")}
              disabled={disabled || parent?.videoCall === false}
            />
          )}
        />
      )}
    </div>
  );
};

export const PreferencesController = (props: {
  name: string;
  label?: string;
  parent?: IPreference;
  disabled?: boolean;
  autoDisabled?: boolean;
}) => {
  const { name, label, parent, disabled, autoDisabled } = props;

  return (
    <div className="col-lg-6 mb-3">
      <Controller
        name={`${name}.enabled`}
        defaultValue={false}
        render={(form) => (
          <FormCheckbox
            form={form}
            label={label}
            disabled={disabled || parent?.enabled === false}
          />
        )}
      />
      <div className="d-flex">
        <PreferencesChannelSettings
          name={name}
          parent={parent?.channel}
          disabled={disabled}
        />
        <PreferencesChannelSettings
          name={name}
          parent={parent?.autoChannel}
          disabled={autoDisabled}
          isAuto
        />
      </div>
    </div>
  );
};
